/*
 * Trove — "Slate Ink" react-admin theme
 * ------------------------------------------------------------------
 * A cool slate + warm-rust theme for react-admin (MUI-based).
 * Works with react-admin v4 and v5.
 *
 * Usage:
 *   import { Admin } from 'react-admin';
 *   import { slateInkTheme } from './slateInkTheme';
 *
 *   <Admin theme={slateInkTheme} ...>...your resources...</Admin>
 *
 * Font: this theme uses "Public Sans". Install it with either
 *   npm i @fontsource/public-sans        // then: import '@fontsource/public-sans'
 * or add to your index.html:
 *   <link href="https://fonts.googleapis.com/css2?family=Public+Sans:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
 *
 * If you don't want the type import, delete the `: RaThemeOptions`
 * annotation and this file becomes plain JS (rename to .js).
 */
import type { RaThemeOptions } from 'react-admin';

/* ---- Slate Ink tokens ---------------------------------------- */
// Exported so components that style outside the MUI palette (e.g. AuthLayout)
// can reference these values instead of duplicating hex codes that would
// drift if the theme changes.
export const slateInkTokens = {
  canvas:       '#ECEFF3', // app background
  surface:      '#FFFFFF', // cards / paper
  border:       '#D3D9E1',
  text:         '#171E27',
  muted:        '#626B78',
  accent:       '#C25A20', // primary (warm rust/amber)
  accentHover:  '#A94D18',
  accentContrast:'#FFFFFF',
  accentBright: '#E5814B', // accent tuned for contrast on dark ink
  accentSoft:   '#F7E6D9', // selected / hover tint
  accentText:   '#9A4718', // text on accent-soft
  appbarBg:     '#161C24', // near-black slate
  appbarText:   '#EEF1F5',
  inkMuted:     'rgba(238,241,245,0.62)', // muted text on appbarBg
  sidebarBg:    '#FBFCFD',
  sidebarText:  '#2E3742',
  sidebarMuted: '#7E8896',
  headerBg:     '#EFF2F6', // table header row
  headerText:   '#626B78',
  rowHover:     '#F4F6F9',
  ok:           '#1F6B40',
  okBg:         '#E6F3EB',
  radius:       4,
  font: `'Public Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif`,
};

const c = slateInkTokens;

export const slateInkTheme: RaThemeOptions = {
  palette: {
    mode: 'light',
    primary:   { main: c.accent, dark: c.accentHover, contrastText: c.accentContrast },
    secondary: { main: c.appbarBg, contrastText: c.appbarText },
    background: { default: c.canvas, paper: c.surface },
    text:      { primary: c.text, secondary: c.muted },
    divider:   c.border,
    success:   { main: c.ok },
    grey: {
      50: '#F6F8FB', 100: c.headerBg, 200: '#E5EAF0',
      300: c.border, 500: c.muted, 700: c.sidebarText, 900: c.text,
    },
  },

  shape: { borderRadius: c.radius },

  typography: {
    fontFamily: c.font,
    fontSize: 13,
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600, fontSize: '1rem' },
    subtitle1: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
  },

  // react-admin sidebar geometry
  sidebar: { width: 250, closedWidth: 56 },

  components: {
    /* ---------- MUI primitives ---------- */
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: c.canvas },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: c.appbarBg,
          color: c.appbarText,
          boxShadow: 'none',
          borderBottom: `1px solid ${c.appbarBg}`,
        },
        // RA's AppBar uses color="secondary" by default
        colorSecondary: { backgroundColor: c.appbarBg, color: c.appbarText },
        colorPrimary:   { backgroundColor: c.appbarBg, color: c.appbarText },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: c.border },
        elevation1: {
          border: `1px solid ${c.border}`,
          boxShadow: '0 1px 2px rgba(20,28,40,.07), 0 10px 28px -16px rgba(20,28,40,.24)',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${c.border}`,
          borderRadius: c.radius,
          boxShadow: '0 1px 2px rgba(20,28,40,.07), 0 10px 28px -16px rgba(20,28,40,.24)',
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: c.radius, textTransform: 'none', fontWeight: 600, paddingInline: 16 },
        contained: {
          backgroundColor: c.accent,
          color: c.accentContrast,
          '&:hover': { backgroundColor: c.accentHover },
        },
        outlined: {
          borderColor: c.accent,
          color: c.accent,
          '&:hover': { borderColor: c.accentHover, backgroundColor: c.accentSoft },
        },
        text: { color: c.accent, '&:hover': { backgroundColor: c.accentSoft } },
      },
    },

    MuiIconButton: {
      styleOverrides: { root: { borderRadius: c.radius } },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: c.radius, fontWeight: 500 },
        outlined: { borderColor: c.border },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: c.radius,
          backgroundColor: c.surface,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.muted },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.accent, borderWidth: 1 },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: { root: { '&.Mui-focused': { color: c.accent } } },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${c.border}`,
          fontSize: 13,
          paddingTop: 10.5,
          paddingBottom: 10.5,
        },
        head: {
          backgroundColor: c.headerBg,
          color: c.headerText,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: c.rowHover } },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: { color: c.sidebarMuted, '&.Mui-checked': { color: c.accent } },
      },
    },

    MuiTablePagination: {
      styleOverrides: { root: { color: c.muted, borderTop: `1px solid ${c.border}` } },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: c.appbarBg, fontSize: 12, borderRadius: c.radius },
      },
    },

    MuiAlert: {
      styleOverrides: {
        standardSuccess: { backgroundColor: c.okBg, color: c.ok },
      },
    },

    /* ---------- react-admin components ---------- */
    RaLayout: {
      styleOverrides: {
        root: {
          backgroundColor: c.canvas,
          '& .RaLayout-content': { backgroundColor: c.canvas, padding: 24 },
        },
      },
    },

    RaSidebar: {
      styleOverrides: {
        root: {
          backgroundColor: c.sidebarBg,
          borderRight: `1px solid ${c.border}`,
          '& .RaSidebar-fixed': { backgroundColor: c.sidebarBg },
        },
      },
    },

    RaMenuItemLink: {
      styleOverrides: {
        root: {
          color: c.sidebarText,
          borderRadius: c.radius,
          margin: '1px 8px',
          fontSize: 14,
          '& .RaMenuItemLink-icon': { color: c.sidebarMuted, minWidth: 34 },
          '&:hover': { backgroundColor: c.rowHover },
          // active menu item
          '&.RaMenuItemLink-active': {
            color: c.accentText,
            fontWeight: 600,
            backgroundColor: c.accentSoft,
            borderLeft: `3px solid ${c.accent}`,
            '& .RaMenuItemLink-icon': { color: c.accentText },
          },
        },
      },
    },

    RaDatagrid: {
      styleOverrides: {
        root: {
          '& .RaDatagrid-headerCell': {
            backgroundColor: c.headerBg,
            color: c.headerText,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
          },
          '& .RaDatagrid-rowCell': { fontSize: 13, color: c.text },
          '& .RaDatagrid-row:hover': { backgroundColor: c.rowHover },
        },
      },
    },

    RaList: {
      styleOverrides: {
        root: { '& .RaList-content': { border: `1px solid ${c.border}`, borderRadius: c.radius } },
      },
    },

    RaFilterForm: {
      styleOverrides: { root: { gap: 8 } },
    },

    RaBulkActionsToolbar: {
      styleOverrides: { toolbar: { backgroundColor: c.accentSoft, color: c.accentText } },
    },
  },
};

export default slateInkTheme;
